import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Button, HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '@artstorefronts/ui';
import { Loader2, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import { CONTENT_REVIEW_REQUESTS_QUERY } from '../../graphql/queries';
import ContentReviewDialog from './ContentReviewDialog';
import CompleteReviewRequestDialog from './CompleteReviewRequestDialog';

interface ReviewRequest {
  id: string;
  userId: string;
  message: string | null;
  createdAt: string;
  user?: {
    asfId?: string;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  profile?: {
    slug?: string | null;
    displayName?: string | null;
  } | null;
}

function fullName(req: ReviewRequest): string {
  if (req.profile?.displayName) return req.profile.displayName;
  const joined = [req.user?.firstName, req.user?.lastName].filter(Boolean).join(' ');
  return joined || req.userId;
}

const PAGE_LIMIT = 50;

function ReviewRequestMessage({ message }: { message: string | null }) {
  if (!message) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <span className="text-sm text-gray-700 line-clamp-2 cursor-default whitespace-pre-wrap break-words">
          {message}
        </span>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          side="top"
          align="start"
          className="w-96 max-h-64 overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap break-words"
        >
          {message}
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}

function ReviewRequestsTable() {
  const { t } = useTranslation('admin');
  const { data, loading, error, refetch, fetchMore } = useQuery(CONTENT_REVIEW_REQUESTS_QUERY, {
    variables: { limit: PAGE_LIMIT, offset: 0 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const [selected, setSelected] = useState<{ userId: string; displayName: string | null } | null>(null);
  const [completing, setCompleting] = useState<{ requestId: string; userLabel: string } | null>(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const rowsLengthRef = useRef(0);

  const rows: ReviewRequest[] = (data?.contentReviewRequests?.edges ?? []).map(
    (edge: { node: ReviewRequest }) => edge.node
  );
  const hasNextPage = data?.contentReviewRequests?.pageInfo?.hasNextPage ?? false;

  rowsLengthRef.current = rows.length;

  const loadMore = useCallback(async () => {
    if (isFetchingMoreRef.current || !hasNextPage) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      await fetchMore({
        variables: {
          limit: PAGE_LIMIT,
          offset: rowsLengthRef.current,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.contentReviewRequests?.edges?.length) return prev;
          return {
            contentReviewRequests: {
              ...fetchMoreResult.contentReviewRequests,
              edges: [...(prev.contentReviewRequests?.edges ?? []), ...fetchMoreResult.contentReviewRequests.edges],
            },
          };
        },
      });
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  }, [hasNextPage, fetchMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 text-red-600 border border-dashed border-red-200 rounded-lg">
        {error.message}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500 border border-dashed border-gray-200 rounded-lg">
        {t('moderation.review-requests.empty')}
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => refetch()} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                {t('moderation.review-requests.col-user')}
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-56">
                {t('moderation.review-requests.col-email')}
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('moderation.review-requests.col-message')}
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                {t('moderation.review-requests.col-submitted')}
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-40 text-right">
                {t('moderation.review-requests.col-action')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {rows.map((req) => {
              const name = fullName(req);
              const submitted = (() => {
                const d = new Date(req.createdAt);
                if (Number.isNaN(d.getTime())) return '—';
                return formatDistanceToNow(d, { addSuffix: true });
              })();
              return (
                <tr key={req.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {req.profile?.slug ? (
                      <a
                        href={`${__TOWN_SQUARE_URL__}/${req.profile.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-gray-900 hover:underline truncate"
                      >
                        {name}
                      </a>
                    ) : (
                      <span className="text-sm font-medium text-gray-900 truncate">{name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 truncate">{req.user?.email ?? '—'}</td>
                  <td className="px-4 py-3">
                    <ReviewRequestMessage message={req.message} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{submitted}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-3"
                        onClick={() =>
                          setCompleting({
                            requestId: req.id,
                            userLabel: name,
                          })
                        }
                      >
                        {t('moderation.review-requests.complete')}
                      </Button>
                      <Button
                        size="sm"
                        className="text-xs h-7 px-3"
                        onClick={() =>
                          setSelected({
                            userId: req.userId,
                            displayName: name,
                          })
                        }
                      >
                        {t('moderation.review-requests.review-content')}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div ref={sentinelRef} className="py-4 flex items-center justify-center">
        {isFetchingMore && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
        {!hasNextPage && rows.length > 0 && (
          <p className="text-xs text-gray-400">{t('moderation.all-loaded', { count: rows.length })}</p>
        )}
      </div>

      {selected && (
        <ContentReviewDialog
          userId={selected.userId}
          displayName={selected.displayName}
          open
          onOpenChange={(open) => {
            if (!open) {
              setSelected(null);
              refetch();
            }
          }}
        />
      )}

      {completing && (
        <CompleteReviewRequestDialog
          requestId={completing.requestId}
          userLabel={completing.userLabel}
          open
          onOpenChange={(open) => {
            if (!open) setCompleting(null);
          }}
          onCompleted={() => refetch()}
        />
      )}
    </>
  );
}

export default ReviewRequestsTable;
